/**
 * DNI/NIE Input Component with Validation
 * Validates Spanish national ID (DNI) and foreign resident ID (NIE)
 */

import { useState, useEffect } from 'react';
import { Input } from './input';
import { Label } from './label';
import { validateSpanishId, formatDniForDisplay } from '@leal-crm/shared/utils/dniUtils';

interface DNIInputProps {
  value: string;
  onValueChange: (dni: string) => void;
  label?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  showValidationType?: boolean;
}

export function DNIInput({
  value,
  onValueChange,
  label,
  error,
  required = false,
  disabled = false,
  placeholder = '12345678A',
  showValidationType = true,
}: DNIInputProps) {
  const [localError, setLocalError] = useState<string | undefined>(error);
  const [validationType, setValidationType] = useState<'DNI' | 'NIE' | null>(null);

  // Update error when prop changes
  useEffect(() => {
    setLocalError(error);
  }, [error]);

  // Update validation type on value change
  useEffect(() => {
    if (value && value.length >= 9) {
      const result = validateSpanishId(value);
      if (result.valid) {
        setValidationType(result.type || null);
      } else {
        setValidationType(null);
      }
    } else {
      setValidationType(null);
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value.toUpperCase();
    // Allow only valid DNI/NIE characters
    newValue = newValue.replace(/[^A-Z0-9]/g, '');
    // Limit to 9 characters
    if (newValue.length > 9) {
      newValue = newValue.slice(0, 9);
    }
    onValueChange(newValue);

    // Clear error on change
    if (localError) {
      setLocalError(undefined);
    }
  };

  const handleBlur = () => {
    if (value) {
      const result = validateSpanishId(value);
      if (!result.valid) {
        // Translate error to Spanish
        let errorMessage = result.error;
        if (result.error?.includes('Invalid format')) {
          errorMessage = 'Formato invalido. Use DNI (8 digitos + letra) o NIE (X/Y/Z + 7 digitos + letra)';
        } else if (result.error?.includes('Invalid check letter')) {
          errorMessage = 'La letra de control no es correcta';
        } else if (result.error?.includes('required')) {
          errorMessage = 'El DNI/NIE es obligatorio';
        }
        setLocalError(errorMessage);
      } else {
        setLocalError(undefined);
      }
    }
  };

  const displayError = localError || error;
  const displayValue = value ? formatDniForDisplay(value) : '';

  return (
    <div className="space-y-1.5">
      {label && (
        <Label htmlFor="dni-input">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </Label>
      )}
      <Input
        id="dni-input"
        type="text"
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        className={displayError ? 'border-red-500' : ''}
        maxLength={10} // Account for hyphen in display
      />
      {displayError && <p className="text-red-500 text-sm">{displayError}</p>}
      {showValidationType && validationType && !displayError && (
        <p className="text-xs text-green-600">
          {validationType === 'DNI' ? 'DNI valido' : 'NIE valido'}
        </p>
      )}
      <p className="text-xs text-gray-400">
        DNI: 8 digitos + letra | NIE: X/Y/Z + 7 digitos + letra
      </p>
    </div>
  );
}

export default DNIInput;
